import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useNav from "core/hooks/useNav";

import { useAuth } from "features/auth/fabric";
import { useStart } from "features/start/fabric";

import api from "api";


export const useMutateRead = () => {
	const { device } = useAuth();

	const nav = useNav();
	return useMutation({
		mutationKey: ["article-read"],
		mutationFn: (contentId) => api.content.read({ contentId, device }),
		onSuccess: ({ url }) => nav.open(url)
	});
};

// BOOKMARKS
export const useQueryBookmarked = () => {
	const start = useStart();

	return useQuery({
		queryKey: ["bookmarked"],
		queryFn: api.content.bookmarked,
		enabled: Boolean(start.user)
	});
};

export const useMutateBookmark = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ contentId, value }) => await api.content.setBookmark({ contentId, value }),
		onMutate: async ({ contentId, value }) => {
			// Cancel outgoing refetches
			await queryClient.cancelQueries({ queryKey: ["top-day", "top-week", "top-featured", "last-seen", "bookmarked"] });

			// Snapshot previous values
			const previousQueries = queryClient.getQueriesData(["top-day", "top-week", "top-featured", "last-seen"]);
			const previousBookmarked = queryClient.getQueryData(["bookmarked"]);

			// Optimistically update
			previousQueries.forEach(([queryKey, queryData]) => {
				if (queryData && Array.isArray(queryData)) {
					const updatedData = queryData.map((item) => (item.id === contentId || item.contentId === contentId) ? { ...item, bookmarked: value } : item);
					if (updatedData.some((item, index) => item !== queryData[index])) queryClient.setQueryData(queryKey, updatedData);
				}
			});

			if (previousBookmarked && Array.isArray(previousBookmarked)) {
				if (value) {
					// Adding to bookmarks - find the content from other queries and add it
					const contentToAdd = previousQueries
						.flatMap(([, data]) => Array.isArray(data) ? data : [])
						.find(item => item.id === contentId || item.contentId === contentId);

					if (contentToAdd) {
						const updatedBookmarked = [...previousBookmarked, { ...contentToAdd, bookmarked: true }];
						queryClient.setQueryData(["bookmarked"], updatedBookmarked);
					}
				} else {
					// Removing from bookmarks
					const updatedBookmarked = previousBookmarked.filter(item => item.id !== contentId && item.contentId !== contentId);
					queryClient.setQueryData(["bookmarked"], updatedBookmarked);
				}
			}

			return { previousQueries, previousBookmarked };
		},
		onError: (err, variables, context) => {
			// Rollback on error
			if (context?.previousQueries) {
				context.previousQueries.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
			}

			if (context?.previousBookmarked !== undefined) {
				queryClient.setQueryData(["bookmarked"], context.previousBookmarked);
			}
		},
		onSuccess: (data) => {
			queryClient.setQueryData(["bookmarked"], data);
		}
	});
};

// GIFT
export const useMutateRefundGift = () => {
	const { device } = useAuth();

	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: ["article-gift-refund"],
		mutationFn: (id) => api.content.gift.refund({ id, device }),
		onSuccess: (_, { id }) => {
			queryClient.invalidateQueries({ queryKey: ["user-history"] });
			queryClient.invalidateQueries({ queryKey: ["start"] });
			queryClient.invalidateQueries({ queryKey: ["gift", id] });
		}
	});
};